import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { newId, slugifyLensId } from "@career-ops-amplify/shared";
import type {
  CandidateGraphDraft,
  JobExperience,
  SkillNode,
  EducationEntry,
  LicenseEntry,
  CertificationEntry,
  NarrativeLens,
  NarrativeLensId,
  WorkAuthorization,
  PersonIdentity,
  CandidatePreferences,
  Engagement,
  ExperienceBullet,
  Achievement,
  AtsPastePack,
} from "@career-ops-amplify/shared";
import {
  useCandidateGraph,
  usePutCandidateGraph,
  usePatchCandidateGraphMeta,
  useAssembleCandidateMarkdown,
  useParseCandidateMarkdown,
  useCommitCandidateDraft,
} from "@/hooks/useApi";
import {
  createCandidateJob,
  updateCandidateJob,
  deleteCandidateJob,
  createCandidateSkill,
  deleteCandidateSkill,
  createCandidateEducation,
  deleteCandidateEducation,
  createCandidateLicense,
  deleteCandidateLicense,
  createCandidateCertification,
  deleteCandidateCertification,
  putCandidateLens,
  deleteCandidateLens,
  getAtsPastePack,
} from "@/api/candidate-graph";
import { cn } from "@/cn";

function emptyJob(): JobExperience {
  return {
    id: newId(),
    employerCanonical: "",
    titleCanonical: "",
    employmentType: "employee",
    isCurrent: false,
    start: "",
    end: "",
    responsibilities: [],
    achievements: [],
    engagements: [],
    technologies: [],
    domains: [],
  };
}

export function ProfileIntelligencePage() {
  const { data, isLoading, error, refetch } = useCandidateGraph();
  const putGraph = usePutCandidateGraph();
  const patchMeta = usePatchCandidateGraphMeta();
  const assemble = useAssembleCandidateMarkdown();
  const parseMd = useParseCandidateMarkdown();
  const commitDraft = useCommitCandidateDraft();

  const [identity, setIdentity] = useState<PersonIdentity>({});
  const [preferences, setPreferences] = useState<CandidatePreferences>({});
  const [workAuth, setWorkAuth] = useState<WorkAuthorization[]>([]);
  const [defaultLens, setDefaultLens] = useState<NarrativeLensId>("");
  const [manualOverride, setManualOverride] = useState(false);
  const [jobDraft, setJobDraft] = useState<JobExperience | null>(null);
  const [skillName, setSkillName] = useState("");
  const [skillAliases, setSkillAliases] = useState("");
  const [skillCategory, setSkillCategory] = useState("domain");
  const [eduForm, setEduForm] = useState({
    institution: "",
    degreeType: "",
    graduationYear: "",
  });
  const [licForm, setLicForm] = useState({ licenseType: "", jurisdiction: "" });
  const [certForm, setCertForm] = useState({ nameCanonical: "", issuer: "" });
  const [lenses, setLenses] = useState<NarrativeLens[]>([]);
  const [newLensLabel, setNewLensLabel] = useState("");
  const [draft, setDraft] = useState<CandidateGraphDraft | null>(null);
  const [pastePack, setPastePack] = useState<AtsPastePack | null>(null);
  const [status, setStatus] = useState<string>("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!data) return;
    setIdentity(data.meta?.identity ?? {});
    setPreferences(data.meta?.preferences ?? {});
    setWorkAuth(data.meta?.workAuthorization ?? []);
    setDefaultLens(data.meta?.defaultLens ?? data.lenses[0]?.id ?? "");
    setManualOverride(Boolean(data.meta?.markdownManualOverride));
    setLenses(
      data.lenses.map((l) => ({
        id: l.id,
        label: l.label || l.id,
        headline: l.headline,
        summary: l.summary,
        proofPolicy: l.proofPolicy,
      })),
    );
  }, [data]);

  const jobCount = data?.jobs.length ?? 0;
  const skillCount = data?.skills.length ?? 0;

  async function saveMeta() {
    setBusy(true);
    setStatus("Saving identity & prefs…");
    try {
      await patchMeta.mutateAsync({
        identity,
        preferences: { ...preferences, narrativeLensDefault: defaultLens },
        workAuthorization: workAuth,
        defaultLens,
        markdownManualOverride: manualOverride,
      });
      setStatus("Saved.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function saveLenses() {
    setBusy(true);
    try {
      for (const lens of lenses) {
        if (!lens.id || !lens.label?.trim()) continue;
        await putCandidateLens(lens.id, lens);
      }
      await refetch();
      setStatus("Lenses saved.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Lens save failed");
    } finally {
      setBusy(false);
    }
  }

  function addLens() {
    const label = newLensLabel.trim();
    if (!label) {
      setStatus("Enter a lens label first (e.g. your positioning name).");
      return;
    }
    let id = slugifyLensId(label);
    if (lenses.some((l) => l.id === id)) {
      id = `${id}-${Date.now().toString(36).slice(-4)}`;
    }
    setLenses((prev) => [...prev, { id, label, headline: "", summary: "" }]);
    setNewLensLabel("");
    if (!defaultLens) setDefaultLens(id);
  }

  async function removeLens(id: string) {
    setBusy(true);
    try {
      await deleteCandidateLens(id);
      setLenses((prev) => prev.filter((l) => l.id !== id));
      if (defaultLens === id) {
        setDefaultLens("");
      }
      await refetch();
      setStatus("Lens removed.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Lens delete failed");
    } finally {
      setBusy(false);
    }
  }

  async function saveJob() {
    if (!jobDraft?.employerCanonical || !jobDraft?.titleCanonical) {
      setStatus("Employer and title are required.");
      return;
    }
    setBusy(true);
    try {
      const existing = data?.jobs.find((j) => j.id === jobDraft.id);
      if (existing) await updateCandidateJob(jobDraft.id, jobDraft);
      else await createCandidateJob(jobDraft);
      setJobDraft(null);
      await refetch();
      setStatus("Job saved; AI Context regenerated.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Job save failed");
    } finally {
      setBusy(false);
    }
  }

  async function removeJob(id: string) {
    setBusy(true);
    try {
      await deleteCandidateJob(id);
      await refetch();
      setStatus("Job deleted.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  async function addSkill() {
    if (!skillName.trim()) return;
    setBusy(true);
    try {
      const skill: SkillNode = {
        id: newId(),
        nameCanonical: skillName.trim(),
        aliases: skillAliases
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        category: skillCategory,
      };
      await createCandidateSkill(skill);
      setSkillName("");
      setSkillAliases("");
      await refetch();
      setStatus("Skill added.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Skill save failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleParse() {
    setBusy(true);
    setStatus("Parsing AI Context markdown (no invent)…");
    try {
      const res = await parseMd.mutateAsync(undefined);
      setDraft(res.draft);
      setStatus(
        `Draft ready: ${(res.draft.jobs ?? []).length} jobs, ${(res.draft.skills ?? []).length} skills. Review before confirm.`,
      );
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Parse failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleCommit() {
    if (!draft) return;
    setBusy(true);
    try {
      await commitDraft.mutateAsync(draft);
      setDraft(null);
      await refetch();
      setStatus("Draft committed to Candidate Intelligence Graph.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Commit failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleAssemble(force = false) {
    setBusy(true);
    try {
      const res = await assemble.mutateAsync({ force, lensId: defaultLens });
      setStatus(
        res.skipped
          ? "Skipped — markdown manual override is on. Force to overwrite."
          : "AI Context regenerated from graph.",
      );
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Assemble failed");
    } finally {
      setBusy(false);
    }
  }

  async function handlePastePack() {
    setBusy(true);
    try {
      const pack = await getAtsPastePack(defaultLens);
      setPastePack(pack);
      setStatus("ATS paste pack ready.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Paste pack failed");
    } finally {
      setBusy(false);
    }
  }

  const locsText = useMemo(
    () =>
      (preferences.preferredLocations ?? [])
        .map((l) => `${l.city}, ${l.country}`)
        .join("\n"),
    [preferences.preferredLocations],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive">
        Failed to load Candidate Intelligence Graph.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      <div className="rounded-lg border border-border bg-card/50 px-4 py-3 text-sm text-muted-foreground">
        <p>
          Itemized source of truth for ATS forms and AI screening.{" "}
          <Link to="/profile/ai-context" className="text-brand hover:underline">
            AI Context
          </Link>{" "}
          markdown is a projection (regenerate from here).{" "}
          <Link to="/assets" className="text-brand hover:underline">
            Assets
          </Link>{" "}
          remain Apply file uploads only.
        </p>
        <p className="mt-2 text-xs">
          Graph: {jobCount} jobs · {skillCount} skills ·{" "}
          {data?.education.length ?? 0} education ·{" "}
          {data?.licenses.length ?? 0} licenses
        </p>
      </div>

      {status && (
        <p className="text-sm text-foreground rounded-md border border-border px-3 py-2">
          {status}
        </p>
      )}

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Import from AI Context
        </h2>
        <p className="text-xs text-muted-foreground">
          Parses current markdown into a draft. Confirm only after review — never
          invents facts.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleParse()}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            Parse markdown → draft
          </button>
          {draft && (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleCommit()}
                className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
              >
                Confirm &amp; commit draft
              </button>
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="rounded-md px-3 py-1.5 text-sm text-muted-foreground"
              >
                Discard draft
              </button>
            </>
          )}
        </div>
        {draft?.warnings && draft.warnings.length > 0 && (
          <ul className="text-xs text-amber-700 dark:text-amber-400 list-disc pl-4">
            {draft.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
        {draft && (
          <pre className="max-h-64 overflow-auto rounded border border-border bg-muted/30 p-3 text-xs">
            {JSON.stringify(draft, null, 2)}
          </pre>
        )}
      </section>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Identity, prefs &amp; work auth
          </h2>
          <button
            type="button"
            disabled={busy}
            onClick={() => void saveMeta()}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            Save
          </button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Preferred name"
            value={identity.preferredName ?? ""}
            onChange={(v) => setIdentity((p) => ({ ...p, preferredName: v }))}
          />
          <Field
            label="Legal name"
            value={identity.legalName ?? ""}
            onChange={(v) => setIdentity((p) => ({ ...p, legalName: v }))}
          />
          <Field
            label="Email"
            value={identity.emails?.[0] ?? ""}
            onChange={(v) =>
              setIdentity((p) => ({ ...p, emails: v ? [v] : [] }))
            }
          />
          <Field
            label="Phone"
            value={identity.phones?.[0] ?? ""}
            onChange={(v) =>
              setIdentity((p) => ({ ...p, phones: v ? [v] : [] }))
            }
          />
          <Field
            label="LinkedIn URL"
            value={identity.linkedinUrl ?? ""}
            onChange={(v) => setIdentity((p) => ({ ...p, linkedinUrl: v }))}
          />
          <Field
            label="Site URL"
            value={identity.siteUrl ?? ""}
            onChange={(v) => setIdentity((p) => ({ ...p, siteUrl: v }))}
          />
          <Field
            label="City"
            value={identity.locationCity ?? ""}
            onChange={(v) => setIdentity((p) => ({ ...p, locationCity: v }))}
          />
          <Field
            label="Country"
            value={identity.locationCountry ?? ""}
            onChange={(v) =>
              setIdentity((p) => ({ ...p, locationCountry: v }))
            }
          />
        </div>
        <label className="block text-xs text-muted-foreground">
          Preferred locations (one per line: City, Country)
          <textarea
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            rows={3}
            value={locsText}
            onChange={(e) => {
              const preferredLocations = e.target.value
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean)
                .map((line, i) => {
                  const [city, ...rest] = line.split(",");
                  return {
                    city: city.trim(),
                    country: rest.join(",").trim() || "",
                    priority: i + 1,
                  };
                });
              setPreferences((p) => ({ ...p, preferredLocations }));
            }}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Work modes (comma: onsite, hybrid, remote)
          <input
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={(preferences.workModes ?? []).join(", ")}
            onChange={(e) => {
              const workModes = e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean) as CandidatePreferences["workModes"];
              setPreferences((p) => ({ ...p, workModes }));
            }}
          />
        </label>
        <div className="flex flex-wrap gap-4 items-center">
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            Default lens
            <select
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
              value={defaultLens}
              onChange={(e) =>
                setDefaultLens(e.target.value as NarrativeLensId)
              }
            >
              <option value="">None</option>
              {lenses.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label || l.id}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            <input
              type="checkbox"
              checked={manualOverride}
              onChange={(e) => setManualOverride(e.target.checked)}
            />
            Manual AI Context override (skip auto-regenerate)
          </label>
        </div>
        <WorkAuthEditor value={workAuth} onChange={setWorkAuth} />
      </section>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <div className="flex justify-between items-center gap-2">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Narrative lenses
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              User-defined positioning views — name them for your walk of life. No fixed role types.
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => void saveLenses()}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            Save lenses
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            placeholder="New lens label (e.g. Platform leader)"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm min-w-[16rem]"
            value={newLensLabel}
            onChange={(e) => setNewLensLabel(e.target.value)}
          />
          <button
            type="button"
            onClick={addLens}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            Add lens
          </button>
        </div>
        {lenses.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No lenses yet. Add one to tailor headlines and summaries per audience.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {lenses.map((lens, idx) => (
              <LensEditor
                key={lens.id}
                value={lens}
                onChange={(next) => {
                  setLenses((prev) => {
                    const copy = [...prev];
                    copy[idx] = next;
                    return copy;
                  });
                }}
                onRemove={() => void removeLens(lens.id)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <div className="flex justify-between items-center gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Work experience
          </h2>
          <button
            type="button"
            onClick={() => setJobDraft(emptyJob())}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            Add job
          </button>
        </div>
        <ul className="space-y-2">
          {(data?.jobs ?? []).map((j) => (
            <li
              key={j.id}
              className="flex flex-wrap items-start justify-between gap-2 rounded border border-border px-3 py-2"
            >
              <div className="text-sm">
                <div className="font-medium text-foreground">
                  {j.titleCanonical} · {j.employerCanonical}
                </div>
                <div className="text-xs text-muted-foreground">
                  {j.start}
                  {j.isCurrent ? "–Present" : j.end ? `–${j.end}` : ""}
                  {(j.engagements?.length ?? 0) > 0 &&
                    ` · ${j.engagements!.length} engagement(s)`}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-xs text-brand"
                  onClick={() => setJobDraft({ ...j })}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="text-xs text-destructive"
                  onClick={() => void removeJob(j.id)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
        {jobDraft && (
          <JobEditor
            job={jobDraft}
            onChange={setJobDraft}
            onSave={() => void saveJob()}
            onCancel={() => setJobDraft(null)}
            busy={busy}
          />
        )}
      </section>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Skills
        </h2>
        <div className="flex flex-wrap gap-2">
          <input
            placeholder="Canonical skill"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={skillName}
            onChange={(e) => setSkillName(e.target.value)}
          />
          <input
            placeholder="Aliases (comma)"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm min-w-[12rem]"
            value={skillAliases}
            onChange={(e) => setSkillAliases(e.target.value)}
          />
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={skillCategory}
            onChange={(e) => setSkillCategory(e.target.value)}
          >
            <option value="technical">technical</option>
            <option value="domain">domain</option>
            <option value="leadership">leadership</option>
            <option value="tool">tool</option>
            <option value="other">other</option>
          </select>
          <button
            type="button"
            disabled={busy}
            onClick={() => void addSkill()}
            className="rounded-md bg-brand px-3 py-1.5 text-sm text-brand-foreground"
          >
            Add
          </button>
        </div>
        <ul className="flex flex-wrap gap-2">
          {(data?.skills ?? []).map((s) => (
            <li
              key={s.id}
              className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs"
            >
              {s.nameCanonical}
              {s.aliases?.length ? ` (${s.aliases.join(", ")})` : ""}
              <button
                type="button"
                className="text-destructive ml-1"
                onClick={() =>
                  void deleteCandidateSkill(s.id).then(() => refetch())
                }
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Education / licenses / certs
        </h2>
        <div className="flex flex-wrap gap-2">
          <input
            placeholder="Institution"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={eduForm.institution}
            onChange={(e) =>
              setEduForm((p) => ({ ...p, institution: e.target.value }))
            }
          />
          <input
            placeholder="Degree"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={eduForm.degreeType}
            onChange={(e) =>
              setEduForm((p) => ({ ...p, degreeType: e.target.value }))
            }
          />
          <input
            placeholder="Year"
            className="w-24 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={eduForm.graduationYear}
            onChange={(e) =>
              setEduForm((p) => ({ ...p, graduationYear: e.target.value }))
            }
          />
          <button
            type="button"
            className="rounded-md border border-border px-3 py-1.5 text-sm"
            onClick={() => {
              if (!eduForm.institution) return;
              const row: EducationEntry = {
                id: newId(),
                ...eduForm,
              };
              void createCandidateEducation(row).then(() => {
                setEduForm({ institution: "", degreeType: "", graduationYear: "" });
                return refetch();
              });
            }}
          >
            Add education
          </button>
        </div>
        <ul className="text-sm space-y-1">
          {(data?.education ?? []).map((e) => (
            <li key={e.id} className="flex justify-between gap-2">
              <span>
                {[e.degreeType, e.institution, e.graduationYear]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
              <button
                type="button"
                className="text-xs text-destructive"
                onClick={() =>
                  void deleteCandidateEducation(e.id).then(() => refetch())
                }
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2 pt-2">
          <input
            placeholder="License type (e.g. P.Eng)"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={licForm.licenseType}
            onChange={(e) =>
              setLicForm((p) => ({ ...p, licenseType: e.target.value }))
            }
          />
          <input
            placeholder="Jurisdiction"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={licForm.jurisdiction}
            onChange={(e) =>
              setLicForm((p) => ({ ...p, jurisdiction: e.target.value }))
            }
          />
          <button
            type="button"
            className="rounded-md border border-border px-3 py-1.5 text-sm"
            onClick={() => {
              if (!licForm.licenseType) return;
              const row: LicenseEntry = { id: newId(), ...licForm };
              void createCandidateLicense(row).then(() => {
                setLicForm({ licenseType: "", jurisdiction: "" });
                return refetch();
              });
            }}
          >
            Add license
          </button>
        </div>
        <ul className="text-sm space-y-1">
          {(data?.licenses ?? []).map((l) => (
            <li key={l.id} className="flex justify-between gap-2">
              <span>
                {l.licenseType}
                {l.jurisdiction ? ` (${l.jurisdiction})` : ""}
              </span>
              <button
                type="button"
                className="text-xs text-destructive"
                onClick={() =>
                  void deleteCandidateLicense(l.id).then(() => refetch())
                }
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2 pt-2">
          <input
            placeholder="Certification"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={certForm.nameCanonical}
            onChange={(e) =>
              setCertForm((p) => ({ ...p, nameCanonical: e.target.value }))
            }
          />
          <input
            placeholder="Issuer"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={certForm.issuer}
            onChange={(e) =>
              setCertForm((p) => ({ ...p, issuer: e.target.value }))
            }
          />
          <button
            type="button"
            className="rounded-md border border-border px-3 py-1.5 text-sm"
            onClick={() => {
              if (!certForm.nameCanonical) return;
              const row: CertificationEntry = { id: newId(), ...certForm };
              void createCandidateCertification(row).then(() => {
                setCertForm({ nameCanonical: "", issuer: "" });
                return refetch();
              });
            }}
          >
            Add certification
          </button>
        </div>
        <ul className="text-sm space-y-1">
          {(data?.certifications ?? []).map((c) => (
            <li key={c.id} className="flex justify-between gap-2">
              <span>
                {c.nameCanonical}
                {c.issuer ? ` — ${c.issuer}` : ""}
              </span>
              <button
                type="button"
                className="text-xs text-destructive"
                onClick={() =>
                  void deleteCandidateCertification(c.id).then(() => refetch())
                }
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Projections
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleAssemble(false)}
            className="rounded-md bg-brand px-3 py-1.5 text-sm text-brand-foreground"
          >
            Regenerate AI Context
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleAssemble(true)}
            className="rounded-md border border-border px-3 py-1.5 text-sm"
          >
            Force regenerate
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handlePastePack()}
            className="rounded-md border border-border px-3 py-1.5 text-sm"
          >
            Build ATS paste pack
          </button>
          {pastePack && (
            <button
              type="button"
              className="rounded-md border border-border px-3 py-1.5 text-sm"
              onClick={() => {
                void navigator.clipboard.writeText(pastePack.plainText);
                setStatus("Paste pack copied to clipboard.");
              }}
            >
              Copy paste pack
            </button>
          )}
        </div>
        {pastePack && (
          <pre className="max-h-80 overflow-auto rounded border border-border bg-muted/30 p-3 text-xs whitespace-pre-wrap">
            {pastePack.plainText}
          </pre>
        )}
      </section>

      {/* keep putGraph available for bulk ops if needed */}
      {putGraph.isError && (
        <p className="text-xs text-destructive">Bulk put error</p>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block text-xs text-muted-foreground">
      {label}
      <input
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function LensEditor({
  value,
  onChange,
  onRemove,
}: {
  value: NarrativeLens;
  onChange: (v: NarrativeLens) => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-2 rounded border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-muted-foreground">
          {value.label || value.id}
          <span className="ml-2 font-normal opacity-70">({value.id})</span>
        </div>
        <button
          type="button"
          className="text-xs text-destructive"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
      <Field
        label="Label"
        value={value.label ?? ""}
        onChange={(label) => onChange({ ...value, label })}
      />
      <Field
        label="Headline"
        value={value.headline ?? ""}
        onChange={(headline) => onChange({ ...value, headline })}
      />
      <label className="block text-xs text-muted-foreground">
        Summary
        <textarea
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          rows={4}
          value={value.summary ?? ""}
          onChange={(e) => onChange({ ...value, summary: e.target.value })}
        />
      </label>
    </div>
  );
}

function WorkAuthEditor({
  value,
  onChange,
}: {
  value: WorkAuthorization[];
  onChange: (v: WorkAuthorization[]) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-xs text-muted-foreground">Work authorization</span>
        <button
          type="button"
          className="text-xs text-brand"
          onClick={() =>
            onChange([
              ...value,
              {
                id: newId(),
                country: "",
                status: "other",
              },
            ])
          }
        >
          Add
        </button>
      </div>
      {value.map((row, idx) => (
        <div key={row.id} className="flex flex-wrap gap-2 items-end">
          <Field
            label="Country"
            value={row.country}
            onChange={(country) => {
              const next = [...value];
              next[idx] = { ...row, country };
              onChange(next);
            }}
          />
          <label className="block text-xs text-muted-foreground">
            Status
            <select
              className="mt-1 block rounded-md border border-border bg-background px-2 py-2 text-sm"
              value={row.status}
              onChange={(e) => {
                const next = [...value];
                next[idx] = { ...row, status: e.target.value };
                onChange(next);
              }}
            >
              <option value="citizen">citizen</option>
              <option value="pr">pr</option>
              <option value="work_visa">work_visa</option>
              <option value="visit">visit</option>
              <option value="other">other</option>
            </select>
          </label>
          <Field
            label="Notes"
            value={row.notes ?? ""}
            onChange={(notes) => {
              const next = [...value];
              next[idx] = { ...row, notes };
              onChange(next);
            }}
          />
          <button
            type="button"
            className="text-xs text-destructive pb-2"
            onClick={() => onChange(value.filter((x) => x.id !== row.id))}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}

function JobEditor({
  job,
  onChange,
  onSave,
  onCancel,
  busy,
}: {
  job: JobExperience;
  onChange: (j: JobExperience) => void;
  onSave: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  function setBullets(
    key: "responsibilities" | "achievements",
    text: string,
  ) {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    if (key === "responsibilities") {
      const responsibilities: ExperienceBullet[] = lines.map((t) => ({
        id: newId(),
        text: t,
      }));
      onChange({ ...job, responsibilities });
    } else {
      const achievements: Achievement[] = lines.map((t) => ({
        id: newId(),
        text: t,
        claimConfidence: "verified",
      }));
      onChange({ ...job, achievements });
    }
  }

  function updateEngagement(idx: number, patch: Partial<Engagement>) {
    const engagements = [...(job.engagements ?? [])];
    engagements[idx] = { ...engagements[idx], ...patch };
    onChange({ ...job, engagements });
  }

  return (
    <div className="space-y-3 rounded-md border border-brand/40 bg-muted/20 p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <Field
          label="Employer"
          value={job.employerCanonical}
          onChange={(employerCanonical) =>
            onChange({ ...job, employerCanonical })
          }
        />
        <Field
          label="Title"
          value={job.titleCanonical}
          onChange={(titleCanonical) => onChange({ ...job, titleCanonical })}
        />
        <Field
          label="Start (YYYY or YYYY-MM)"
          value={job.start ?? ""}
          onChange={(start) => onChange({ ...job, start })}
        />
        <Field
          label="End"
          value={job.end ?? ""}
          onChange={(end) => onChange({ ...job, end: end || null })}
        />
        <label className="text-xs text-muted-foreground flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(job.isCurrent)}
            onChange={(e) =>
              onChange({
                ...job,
                isCurrent: e.target.checked,
                end: e.target.checked ? null : job.end,
              })
            }
          />
          Current role
        </label>
        <label className="text-xs text-muted-foreground">
          Employment type
          <select
            className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
            value={job.employmentType ?? "employee"}
            onChange={(e) =>
              onChange({ ...job, employmentType: e.target.value })
            }
          >
            <option value="employee">employee</option>
            <option value="contractor">contractor</option>
            <option value="founder">founder</option>
            <option value="advisory">advisory</option>
            <option value="other">other</option>
          </select>
        </label>
        <Field
          label="City"
          value={job.locationCity ?? ""}
          onChange={(locationCity) => onChange({ ...job, locationCity })}
        />
        <Field
          label="Country"
          value={job.locationCountry ?? ""}
          onChange={(locationCountry) => onChange({ ...job, locationCountry })}
        />
      </div>
      <label className="block text-xs text-muted-foreground">
        Scope summary
        <textarea
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          rows={2}
          value={job.scopeSummary ?? ""}
          onChange={(e) => onChange({ ...job, scopeSummary: e.target.value })}
        />
      </label>
      <label className="block text-xs text-muted-foreground">
        Responsibilities (one per line)
        <textarea
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          rows={4}
          value={(job.responsibilities ?? []).map((r) => r.text).join("\n")}
          onChange={(e) => setBullets("responsibilities", e.target.value)}
        />
      </label>
      <label className="block text-xs text-muted-foreground">
        Achievements (one per line — metric-bearing preferred)
        <textarea
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          rows={4}
          value={(job.achievements ?? []).map((a) => a.text).join("\n")}
          onChange={(e) => setBullets("achievements", e.target.value)}
        />
      </label>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            Client engagements (e.g. BMO under FazeZERO)
          </span>
          <button
            type="button"
            className="text-xs text-brand"
            onClick={() =>
              onChange({
                ...job,
                engagements: [
                  ...(job.engagements ?? []),
                  {
                    id: newId(),
                    endClient: "",
                    title: "",
                    start: "",
                    end: "",
                    domains: [],
                    responsibilities: [],
                    achievements: [],
                  },
                ],
              })
            }
          >
            Add engagement
          </button>
        </div>
        {(job.engagements ?? []).map((eng, idx) => (
          <div
            key={eng.id}
            className={cn("grid gap-2 sm:grid-cols-2 rounded border border-border p-2")}
          >
            <Field
              label="End client"
              value={eng.endClient}
              onChange={(endClient) => updateEngagement(idx, { endClient })}
            />
            <Field
              label="Title"
              value={eng.title ?? ""}
              onChange={(title) => updateEngagement(idx, { title })}
            />
            <Field
              label="Start"
              value={eng.start ?? ""}
              onChange={(start) => updateEngagement(idx, { start })}
            />
            <Field
              label="End"
              value={eng.end ?? ""}
              onChange={(end) => updateEngagement(idx, { end: end || null })}
            />
            <label className="block text-xs text-muted-foreground sm:col-span-2">
              Domains (comma)
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={(eng.domains ?? []).join(", ")}
                onChange={(e) =>
                  updateEngagement(idx, {
                    domains: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </label>
            <button
              type="button"
              className="text-xs text-destructive"
              onClick={() =>
                onChange({
                  ...job,
                  engagements: (job.engagements ?? []).filter(
                    (x) => x.id !== eng.id,
                  ),
                })
              }
            >
              Remove engagement
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onSave}
          className="rounded-md bg-brand px-3 py-1.5 text-sm text-brand-foreground"
        >
          Save job
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-3 py-1.5 text-sm text-muted-foreground"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
